/**
 * Item Create Overview Widget
 * 
 * Product Images section for create mode.
 * Uses ProductGalleryWidget in draft mode with staged media from ItemCreateMediaContext.
 * 
 * Self-healing: If provider is missing (e.g., HMR edge case), creates its own provider
 * to prevent app crash. In normal flow, uses the shared provider from ItemDetailPage.
 */

import React from 'react';
import { ProductGalleryWidget } from './ProductGalleryWidget';
import { useOptionalItemCreateMedia, ItemCreateMediaProvider, useItemCreateMedia } from '../../../../contexts/ItemCreateMediaContext';
import type { ShopifySectionWidgetProps } from '../types';

/**
 * Inner component that uses the context (assumes provider exists)
 */
function ItemCreateOverviewWidgetInner(props: ShopifySectionWidgetProps) {
  const { stagedMedia, addMedia, removeMedia, setPrimary, reorder, removeMany } = useItemCreateMedia();

  // Convert staged media to draft media format for ProductGalleryWidget
  const draftMedia = React.useMemo(() => {
    return stagedMedia.map((item) => ({
      file_url: item.file_url,
      file_name: item.file_name,
      localPreviewUrl: item.localPreviewUrl,
      is_primary_image: item.is_primary_image ?? false,
      idx: item.idx ?? 0,
    }));
  }, [stagedMedia]);

  return (
    <ProductGalleryWidget
      {...props}
      isDraft={true}
      draftMedia={draftMedia}
      onDraftAddMedia={addMedia}
      onDraftRemoveMedia={removeMedia}
      onDraftSetPrimary={setPrimary}
      onDraftReorder={reorder}
      onDraftRemoveMany={removeMany}
    />
  );
}


/**
 * Wrapper that checks for provider and self-heals if missing
 */
export function ItemCreateOverviewWidget(props: ShopifySectionWidgetProps) {
  const context = useOptionalItemCreateMedia();

  // If provider exists, use it (normal flow - shared state with sidebar)
  if (context) {
    return <ItemCreateOverviewWidgetInner {...props} />;
  }

  // Provider missing (HMR edge case or unexpected render) - create fallback provider
  // This prevents app crash but creates isolated state (better than crashing)
  if (import.meta.env.DEV) {
    console.warn(
      '[ItemCreateOverviewWidget] Provider missing - creating fallback provider. ' +
      'This indicates ItemDetailPage did not wrap with ItemCreateMediaProvider. ' +
      'State will be isolated (sidebar and main area won\'t share staged media).'
    );
  }
  return (
    <ItemCreateMediaProvider>
      <ItemCreateOverviewWidgetInner {...props} />
    </ItemCreateMediaProvider>
  );
}
